import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { PropertyService, Property } from '../../services/property.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { FavoritesService } from '../../services/favorites.service';

@Component({
  selector: 'app-properties',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PaginationComponent],
  templateUrl: './properties.component.html',
  styleUrl: './properties.component.css'
})
export class PropertiesComponent implements OnInit {
  properties = signal<Property[]>([]);
  loading = signal(true);
  error = signal('');
  searchQuery = signal('');
  
  // Filters
  listingTypeFilter = signal<'all' | 'sale' | 'rent'>('all');
  propertyTypeFilter = signal<string>('all');
  cityFilter = signal<string>('all');
  countryFilter = signal<string>('all');
  showFavoritesOnly = signal(false);
  minPrice = signal<number | null>(null);
  maxPrice = signal<number | null>(null);
  minBedrooms = signal<number | null>(null);
  maxBedrooms = signal<number | null>(null);
  minBathrooms = signal<number | null>(null);
  maxBathrooms = signal<number | null>(null);
  minArea = signal<number | null>(null);
  maxArea = signal<number | null>(null);
  minYearBuilt = signal<number | null>(null);
  maxYearBuilt = signal<number | null>(null);
  neighborhoodFilter = signal<string>('all');
  zoneFilter = signal<string>('all');
  
  // Amenity filters
  hasElevatorFilter = signal<boolean | null>(null);
  hasParkingFilter = signal<boolean | null>(null);
  isFurnishedFilter = signal<boolean | null>(null);
  hasTerraceFilter = signal<boolean | null>(null);
  hasGardenFilter = signal<boolean | null>(null);
  hasViewFilter = signal<boolean | null>(null);
  
  sortBy = signal<'price-asc' | 'price-desc' | 'newest' | 'oldest' | 'area-asc' | 'area-desc' | 'bedrooms' | 'bathrooms'>('newest');
  
  // Available options for dropdowns
  availableCities = signal<string[]>([]);
  availableCountries = signal<string[]>([]);
  availablePropertyTypes = signal<string[]>([]);
  availableNeighborhoods = signal<string[]>([]);
  availableZones = signal<string[]>([]);
  
  // UI state
  showAdvancedFilters = signal(false);
  showMobileFilters = signal(false);
  
  // Pagination
  currentPage = signal(1);
  itemsPerPage = 15;
  Math = Math; // Expose Math to template
  
  currentImageIndices = signal<Map<string, number>>(new Map());
  propertyImages = signal<Map<string, any[]>>(new Map());
  propertyImageIndices: { [key: number]: number } = {};

  constructor(
    public propertyService: PropertyService,
    private router: Router,
    public favoritesService: FavoritesService
  ) {}

  ngOnInit() {
    this.loadProperties();
  }

  private async loadProperties() {
    try {
      this.loading.set(true);
      this.error.set('');

      const data = await this.propertyService.loadPublishedProperties();
      
      // Ensure property_images are properly loaded
      const propertiesWithImages = data.map(prop => ({
        ...prop,
        property_images: prop.property_images || []
      }));
      
      this.properties.set(propertiesWithImages);
      
      // Extract unique values for filters
      const cities = [...new Set(propertiesWithImages.map(p => p.city).filter(c => c && c.trim() !== ''))].sort() as string[];
      const countries = [...new Set(propertiesWithImages.map(p => p.country).filter(c => c && c.trim() !== ''))].sort() as string[];
      const propertyTypes = [...new Set(propertiesWithImages.map(p => p.property_type).filter(t => t && t.trim() !== ''))].sort() as string[];
      const neighborhoods = [...new Set(propertiesWithImages.map(p => p.neighborhood).filter(n => n && n.trim() !== ''))].sort() as string[];
      const zones = [...new Set(propertiesWithImages.map(p => p.zone).filter(z => z && z.trim() !== ''))].sort() as string[];
      
      this.availableCities.set(cities);
      this.availableCountries.set(countries);
      this.availablePropertyTypes.set(propertyTypes);
      this.availableNeighborhoods.set(neighborhoods);
      this.availableZones.set(zones);
      
      // Pre-load all image URLs
      await this.propertyService.preloadImageUrls(propertiesWithImages);
    } catch (err: any) {
      this.error.set(err.message || 'Failed to load properties');
      console.error('Error:', err);
    } finally {
      this.loading.set(false);
    }
  }

  private async preloadAllCarouselImages(properties: Property[]): Promise<void> {
    for (const prop of properties) {
      if (prop.property_images && prop.property_images.length > 0) {
        for (const img of prop.property_images) {
          await this.propertyService.getImageUrl(img.storage_path);
        }
      }
    }
  }

  get filteredProperties(): Property[] {
    let filtered = this.propertyService.filterProperties(this.properties(), this.searchQuery());
    
    // Apply favorites filter first
    if (this.showFavoritesOnly()) {
      const favoriteIds = this.favoritesService.getFavoriteIds();
      filtered = filtered.filter(p => favoriteIds.includes(p.id));
    }
    
    // Apply listing type filter
    if (this.listingTypeFilter() !== 'all') {
      filtered = filtered.filter(p => p.listing_type.toLowerCase() === this.listingTypeFilter());
    }
    
    // Apply property type filter
    if (this.propertyTypeFilter() !== 'all') {
      filtered = filtered.filter(p => p.property_type === this.propertyTypeFilter());
    }
    
    // Apply city filter
    if (this.cityFilter() !== 'all') {
      filtered = filtered.filter(p => p.city === this.cityFilter());
    }
    
    // Apply country filter
    if (this.countryFilter() !== 'all') {
      filtered = filtered.filter(p => p.country === this.countryFilter());
    }
    
    // Apply price range filter
    if (this.minPrice() !== null) {
      filtered = filtered.filter(p => p.price >= this.minPrice()!);
    }
    if (this.maxPrice() !== null) {
      filtered = filtered.filter(p => p.price <= this.maxPrice()!);
    }
    
    // Apply bedrooms filter
    if (this.minBedrooms() !== null) {
      filtered = filtered.filter(p => (p.bedrooms || 0) >= this.minBedrooms()!);
    }
    if (this.maxBedrooms() !== null) {
      filtered = filtered.filter(p => (p.bedrooms || 0) <= this.maxBedrooms()!);
    }
    
    // Apply bathrooms filter
    if (this.minBathrooms() !== null) {
      filtered = filtered.filter(p => (p.bathrooms || 0) >= this.minBathrooms()!);
    }
    if (this.maxBathrooms() !== null) {
      filtered = filtered.filter(p => (p.bathrooms || 0) <= this.maxBathrooms()!);
    }
    
    // Apply area filter
    if (this.minArea() !== null) {
      filtered = filtered.filter(p => (p.area || 0) >= this.minArea()!);
    }
    if (this.maxArea() !== null) {
      filtered = filtered.filter(p => (p.area || 0) <= this.maxArea()!);
    }
    
    // Apply year built filter
    if (this.minYearBuilt() !== null) {
      filtered = filtered.filter(p => (p.year_built || 0) >= this.minYearBuilt()!);
    }
    if (this.maxYearBuilt() !== null) {
      filtered = filtered.filter(p => (p.year_built || 9999) <= this.maxYearBuilt()!);
    }
    
    // Apply neighborhood filter
    if (this.neighborhoodFilter() !== 'all') {
      filtered = filtered.filter(p => p.neighborhood === this.neighborhoodFilter());
    }
    
    // Apply zone filter
    if (this.zoneFilter() !== 'all') {
      filtered = filtered.filter(p => p.zone === this.zoneFilter());
    }
    
    // Apply amenity filters
    if (this.hasElevatorFilter() !== null) {
      filtered = filtered.filter(p => p.has_elevator === this.hasElevatorFilter());
    }
    if (this.hasParkingFilter() !== null) {
      filtered = filtered.filter(p => ((p.parking_covered || 0) + (p.parking_outdoor || 0)) > 0 === this.hasParkingFilter());
    }
    if (this.isFurnishedFilter() !== null) {
      filtered = filtered.filter(p => p.is_furnished === this.isFurnishedFilter());
    }
    if (this.hasTerraceFilter() !== null) {
      filtered = filtered.filter(p => p.has_terrace === this.hasTerraceFilter());
    }
    if (this.hasGardenFilter() !== null) {
      filtered = filtered.filter(p => p.has_garden === this.hasGardenFilter());
    }
    if (this.hasViewFilter() !== null) {
      filtered = filtered.filter(p => p.has_view === this.hasViewFilter());
    }
    
    // Apply sorting
    const sortBy = this.sortBy();
    if (sortBy === 'price-asc') {
      filtered = [...filtered].sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-desc') {
      filtered = [...filtered].sort((a, b) => b.price - a.price);
    } else if (sortBy === 'newest') {
      filtered = [...filtered].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortBy === 'oldest') {
      filtered = [...filtered].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (sortBy === 'area-asc') {
      filtered = [...filtered].sort((a, b) => (a.area || 0) - (b.area || 0));
    } else if (sortBy === 'area-desc') {
      filtered = [...filtered].sort((a, b) => (b.area || 0) - (a.area || 0));
    } else if (sortBy === 'bedrooms') {
      filtered = [...filtered].sort((a, b) => (b.bedrooms || 0) - (a.bedrooms || 0));
    } else if (sortBy === 'bathrooms') {
      filtered = [...filtered].sort((a, b) => (b.bathrooms || 0) - (a.bathrooms || 0));
    }
    
    return filtered;
  }

  get paginatedProperties(): Property[] {
    const filtered = this.filteredProperties;
    const startIndex = (this.currentPage() - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredProperties.length / this.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const pages = [];
    const total = this.totalPages;
    const current = this.currentPage();
    
    // Show max 7 page numbers with ellipsis
    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 3) {
        // Near start: 1 2 3 4 ... total
        pages.push(1, 2, 3, 4, -1, total);
      } else if (current >= total - 2) {
        // Near end: 1 ... total-3 total-2 total-1 total
        pages.push(1, -1, total - 3, total - 2, total - 1, total);
      } else {
        // Middle: 1 ... current-1 current current+1 ... total
        pages.push(1, -1, current - 1, current, current + 1, -1, total);
      }
    }
    
    return pages;
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      // Scroll to top of properties section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage() {
    if (this.currentPage() < this.totalPages) {
      this.goToPage(this.currentPage() + 1);
    }
  }

  previousPage() {
    if (this.currentPage() > 1) {
      this.goToPage(this.currentPage() - 1);
    }
  }

  setListingTypeFilter(type: 'all' | 'sale' | 'rent') {
    this.listingTypeFilter.set(type);
  }

  setPropertyTypeFilter(type: string) {
    this.propertyTypeFilter.set(type);
  }

  setCityFilter(city: string) {
    this.cityFilter.set(city);
  }

  setCountryFilter(country: string) {
    this.countryFilter.set(country);
  }

  setSortBy(sort: 'price-asc' | 'price-desc' | 'newest' | 'oldest' | 'area-asc' | 'area-desc' | 'bedrooms' | 'bathrooms') {
    this.sortBy.set(sort);
  }

  toggleAdvancedFilters() {
    this.showAdvancedFilters.set(!this.showAdvancedFilters());
  }

  toggleMobileFilters() {
    this.showMobileFilters.set(!this.showMobileFilters());
  }

  clearFilters() {
    this.searchQuery.set('');
    this.listingTypeFilter.set('all');
    this.propertyTypeFilter.set('all');
    this.cityFilter.set('all');
    this.countryFilter.set('all');
    this.neighborhoodFilter.set('all');
    this.zoneFilter.set('all');
    this.showFavoritesOnly.set(false);
    this.minPrice.set(null);
    this.maxPrice.set(null);
    this.minBedrooms.set(null);
    this.maxBedrooms.set(null);
    this.minBathrooms.set(null);
    this.maxBathrooms.set(null);
    this.minArea.set(null);
    this.maxArea.set(null);
    this.minYearBuilt.set(null);
    this.maxYearBuilt.set(null);
    this.hasElevatorFilter.set(null);
    this.hasParkingFilter.set(null);
    this.isFurnishedFilter.set(null);
    this.hasTerraceFilter.set(null);
    this.hasGardenFilter.set(null);
    this.hasViewFilter.set(null);
    this.currentPage.set(1);
    this.sortBy.set('newest');
  }

  getImageUrlFromCache(storagePath: string): string {
    return this.propertyService.getImageUrlFromCache(storagePath);
  }

  viewPropertyDetail(propertyId: string) {
    this.router.navigate(['/property', propertyId]);
  }

  initializeCarousel(property: Property) {
    const indices = this.currentImageIndices();
    if (!indices.has(property.id)) {
      indices.set(property.id, 0);
      this.currentImageIndices.set(new Map(indices));
    }

    const images = this.propertyImages();
    if (!images.has(property.id)) {
      if (property.property_images && property.property_images.length > 0) {
        // Ensure images are sorted by position and all are included (including primary)
        const sortedImages = [...property.property_images].sort((a, b) => a.position - b.position);
        images.set(property.id, sortedImages);
        this.propertyImages.set(new Map(images));
        
        // Preload all carousel image URLs and store them in cache
        this.preloadCarouselImages(sortedImages);
      } else {
        // Initialize with empty array if no images
        images.set(property.id, []);
        this.propertyImages.set(new Map(images));
      }
    }
  }

  private async preloadCarouselImages(images: any[]): Promise<void> {
    const urls = this.propertyService.imageUrls();
    for (const img of images) {
      if (!urls.has(img.storage_path)) {
        try {
          const url = await this.propertyService.getImageUrl(img.storage_path);
          if (url) {
            urls.set(img.storage_path, url);
          }
        } catch (err) {
          console.error('Failed to preload carousel image:', err);
        }
      }
    }
    this.propertyService.imageUrls.set(new Map(urls));
  }

  getCurrentImage(property: Property) {
    const images = this.propertyImages().get(property.id);
    if (!images || images.length === 0) {
      // Fall back to primary image
      const primaryImage = this.propertyService.getPrimaryImage(property);
      return primaryImage ? this.getImageUrlFromCache(primaryImage.storage_path) : '';
    }
    
    const currentIndex = this.currentImageIndices().get(property.id) || 0;
    const currentImage = images[currentIndex];
    if (currentImage) {
      const url = this.getImageUrlFromCache(currentImage.storage_path);
      return url || '';
    }
    return '';
  }

  getImageCount(property: Property): number {
    const images = this.propertyImages().get(property.id);
    return images ? images.length : 0;
  }

  getCurrentImageIndex(property: Property): number {
    return (this.currentImageIndices().get(property.id) || 0) + 1;
  }

  nextImage(event: Event, property: Property) {
    event.stopPropagation();
    const images = this.propertyImages().get(property.id);
    if (!images) return;

    const indices = this.currentImageIndices();
    const currentIndex = indices.get(property.id) || 0;
    const nextIndex = (currentIndex + 1) % images.length;
    indices.set(property.id, nextIndex);
    this.currentImageIndices.set(new Map(indices));
  }

  previousImage(event: Event, property: Property) {
    event.stopPropagation();
    const images = this.propertyImages().get(property.id);
    if (!images) return;

    const indices = this.currentImageIndices();
    const currentIndex = indices.get(property.id) || 0;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    indices.set(property.id, prevIndex);
    this.currentImageIndices.set(new Map(indices));
  }

  getPropertyImages(property: Property) {
    return property.property_images || [];
  }

  getPropertyImageUrl(property: Property, propertyIdx: number): string {
    const images = this.getPropertyImages(property);
    if (!images || images.length === 0) return '';
    
    // Initialize to primary image index if not set
    if (this.propertyImageIndices[propertyIdx] === undefined) {
      const primaryImage = this.propertyService.getPrimaryImage(property);
      if (primaryImage) {
        const primaryIndex = images.findIndex(img => img.id === primaryImage.id);
        this.propertyImageIndices[propertyIdx] = primaryIndex >= 0 ? primaryIndex : 0;
      } else {
        this.propertyImageIndices[propertyIdx] = 0;
      }
    }
    
    const currentIndex = this.propertyImageIndices[propertyIdx];
    const image = images[currentIndex];
    
    if (!image || !image.storage_path) return '';
    
    return this.getImageUrlFromCache(image.storage_path);
  }

  nextPropertyImage(propertyIdx: number, event: Event) {
    event.stopPropagation();
    const images = this.getPropertyImages(this.properties()[propertyIdx]);
    const currentIndex = this.propertyImageIndices[propertyIdx] || 0;
    this.propertyImageIndices[propertyIdx] = (currentIndex + 1) % images.length;
  }

  previousPropertyImage(propertyIdx: number, event: Event) {
    event.stopPropagation();
    const images = this.getPropertyImages(this.properties()[propertyIdx]);
    const currentIndex = this.propertyImageIndices[propertyIdx] || 0;
    this.propertyImageIndices[propertyIdx] = (currentIndex - 1 + images.length) % images.length;
  }

  toggleFavorite(propertyId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.favoritesService.toggleFavorite(propertyId);
  }

  isFavorite(propertyId: string): boolean {
    return this.favoritesService.isFavorite(propertyId);
  }

  toggleFavoritesFilter() {
    this.showFavoritesOnly.set(!this.showFavoritesOnly());
    this.currentPage.set(1); // Reset to first page when filter changes
  }
}
